// Hidden switches that pick this browser's theme until pressed again: a season's at any time of year, or the regular one
// mid-season. Mac: Cmd+Ctrl+Option+H Halloween, Cmd+Ctrl+Option+X Christmas, Cmd+Ctrl+Option+A regular; elsewhere
// Ctrl+Shift+Alt+ the same letter. Admin uses these letters without Option/Alt, but in Talk those combos accept,
// reject and hang up calls. One at a time, and white-label domains stay unbranded either way. api-core's /get-statics
// reads season_demo, so both apps show the same season.

export const SEASON_HALLOWEEN = 'halloween'
export const SEASON_XMAS = 'xmas'
export const SEASON_NONE = 'none'

const STORAGE_KEY = 'season_demo'
const STATICS_URL = '/get-statics'
const SEASON_KEYS = {
  KeyH: SEASON_HALLOWEEN,
  KeyX: SEASON_XMAS,
  KeyA: SEASON_NONE
}

/**
 * The season this browser is demoing, SEASON_NONE for the regular theme, or null to follow the calendar.
 *
 * @returns {?string}
 */
export function seasonDemo () {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch (error) {
    return null
  }
}

function isMac () {
  const platform =
        navigator.userAgentData?.platform || navigator.platform || ''
  return /mac|iphone|ipad/i.test(platform)
}

// The modifiers held with H, X or A, in KEYBOARD_SHORTCUTS' notation. Chrome on a Mac keeps Cmd+Shift+H for its Home page,
// so the Mac set pairs Cmd with Ctrl instead.
export const SEASON_DEMO_MODIFIERS = {
  mac: ['Ctrl', 'Cmd', 'Alt'],
  windows: ['Ctrl', 'Shift', 'Alt']
}

/**
 * The demo shortcuts for one platform, as KEYBOARD_SHORTCUTS writes them, such as 'Ctrl+Shift+Alt+H'.
 *
 * @param {'mac'|'windows'} platform
 * @returns {string[]}
 */
export function seasonDemoShortcuts (platform) {
  return Object.keys(SEASON_KEYS).map((code) => [...SEASON_DEMO_MODIFIERS[platform], code.replace('Key', '')].join('+'))
}

function demoSeason (event) {
  const modifiers = isMac()
    ? event.metaKey && event.ctrlKey && event.altKey && !event.shiftKey
    : event.ctrlKey && event.shiftKey && event.altKey && !event.metaKey

  return modifiers ? (SEASON_KEYS[event.code] ?? null) : null
}

/**
 * Asks /get-statics for the demoed season's theme. An axios request interceptor.
 *
 * @param {Object} config
 * @returns {Object}
 */
export function withSeasonDemo (config) {
  const season = seasonDemo()

  if (config.url === STATICS_URL && season) {
    config.params = { ...config.params, season_demo: season }
  }

  return config
}

/**
 * Turns a season's demo on, or off when it is already on, then calls apply so every logo and color picks it up.
 * Talk passes a statics re-fetch: reloading the page would end a live call.
 *
 * @param {Function} apply
 * @returns {Function} Stops listening
 */
export function listenForSeasonDemo (apply) {
  const toggle = (event) => {
    const season = demoSeason(event)

    if (!season) {
      return
    }

    event.preventDefault()

    try {
      if (seasonDemo() === season) {
        localStorage.removeItem(STORAGE_KEY)
      } else {
        localStorage.setItem(STORAGE_KEY, season)
      }
    } catch (error) {
      return
    }

    apply()
  }

  document.addEventListener('keydown', toggle)

  return () => document.removeEventListener('keydown', toggle)
}

/**
 * Marks the page for the season the statics name, so the shell's tokens turn pumpkin in Halloween week. A white-label
 * brand keeps its own colors.
 *
 * @param {?Object} statics
 * @param {HTMLElement} root
 */
export function applySeason (statics, root = document.documentElement) {
  root.classList.toggle('is-halloween', Boolean(statics?.halloween_enabled) && !statics?.whitelabel)
}
